import {AttachMoney, CreditCard, ReceiptLong, Splitscreen} from '@mui/icons-material';
import History from '@mui/icons-material/History';
import HomeWork from '@mui/icons-material/HomeWork';
import {useMediaQuery, useTheme} from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import dayjs from 'dayjs';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {privileges} from 'utils/constants/privileges';
import strings from '../../../../common/Translation/Translate';
import {Privilege} from '../../../../utils/redux/reducer/authentication-slice';
import {TabPanel} from '../../../../utils/validators/HelperComponents';
import '../styles.scss';
import ActualRevenue from '../../Actualization/ActualRevenue';
import BookingHistory from './BookingHistory';
import {BookingLogs} from './BookingLogs';
import PaymentDetails from './PaymentDetails';
import VenueCustomerDetails from './VenueCustomerDetails';
import SplitRevenue from '../../Actualization/SplitRevenue';
import {useParams} from 'react-router-dom';
import type { LineItemBillingPaymentState } from './BookingBillingPaymentPanels';

interface BookingTableInfoProps {
    bookingLineItem: any;
    onSuccess: () => void;
    isPastActualizationWindow: boolean;
    isWithinActualizationWindow: boolean;
    relatedBookingLineItems: any[];
    billingPayment: LineItemBillingPaymentState;
}

const BookingDetailTabs = (
    {
        bookingLineItem,
        onSuccess,
        isPastActualizationWindow,
        isWithinActualizationWindow,
        relatedBookingLineItems,
        billingPayment,
    }: BookingTableInfoProps) => {
    const {id} = useParams();
    const theme = useTheme();
    const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const IsPrivilege = useSelector(Privilege);
    const [value, setValue] = React.useState(0);

    const showPaymentTab =
        !bookingLineItem.deleted && String(bookingLineItem.status || '').toUpperCase() === 'CONFIRMED';

    const showActualRevenueTab =
        !bookingLineItem.deleted &&
        bookingLineItem.status === 'CONFIRMED' &&
        dayjs(bookingLineItem.startDatetime).isBefore(new Date()) &&
        IsPrivilege.includes(privileges.add_actualization);

    useEffect(() => {
        setValue(0);
    }, [id, bookingLineItem?.status]);

    const customLabel = (label: string, Icon: React.ReactNode) => (
        <span className="tab-header">{Icon}&nbsp;{label}</span>
    );

    const tabItems = [
        {
            key: 'venueCustomer',
            label: strings.tabs_text_venueCustomerDetails,
            icon: <HomeWork/>,
            render: () => (
                <VenueCustomerDetails
                    bookingLineItem={bookingLineItem}
                    onSuccess={onSuccess}
                    isDeleted={bookingLineItem.deleted}
                    billingPayment={billingPayment}
                />
            ),
        },
        {
            key: 'payment',
            label: strings.tabs_text_paymentDetails,
            icon: <CreditCard/>,
            condition: showPaymentTab,
            render: () => (
                <PaymentDetails
                    bookingLineItem={bookingLineItem}
                    permission={IsPrivilege}
                    onSuccess={onSuccess}
                />
            ),
        },
        {
            key: 'actualRevenue',
            label: strings.tabs_text_actual_revenue,
            icon: <AttachMoney/>,
            condition: showActualRevenueTab,
            render: () => (
                <ActualRevenue
                    bookingLineItem={bookingLineItem}
                    onSuccess={onSuccess}
                    isWithinActualizationWindow={
                        isWithinActualizationWindow && bookingLineItem.status === 'CONFIRMED'
                    }
                    isPastActualizationWindow={isPastActualizationWindow}
                />
            ),
        },
        {
            key: 'splitRevenue',
            label: strings.splitRevenue,
            icon: <Splitscreen/>,
            condition:
                IsPrivilege?.includes(privileges.splitRevenue) &&
                showActualRevenueTab &&
                (relatedBookingLineItems || []).filter(
                    (item) => String(item?.status || '').toUpperCase() !== 'CANCELLED',
                ).length > 1,
            render: () => (
                <SplitRevenue bookingLineItem={bookingLineItem} onSuccess={onSuccess}/>
            ),
        },
        {
            key: 'log',
            label: strings.tabs_text_logDetails,
            icon: <ReceiptLong/>,
            condition: IsPrivilege.includes(privileges.add_actualization),
            render: () => <BookingLogs bookingLineItem={bookingLineItem}/>,
        },
        {
            key: 'history',
            label: strings.tabs_text_historyDetails,
            icon: <History/>,
            render: () => <BookingHistory bookingLineItem={bookingLineItem}/>,
        },
    ];

    const visibleTabs = tabItems.filter((item) => item.condition === undefined || item.condition);

    return (
        <Box p={0} sx={{width: '100%', '.MuiBox-root': {px: 0}}}>
            <Box sx={{
                borderBottom: 1,
                borderColor: 'divider',
            }}>
                <Tabs
                    value={value}
                    onChange={(e, val) => setValue(val)}
                    aria-label="basic tabs example"
                    variant="scrollable"
                    allowScrollButtonsMobile
                    scrollButtons="auto"
                    sx={{
                        '.MuiTabScrollButton-root.Mui-disabled': {
                            display: 'none',
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            minHeight: 44,
                            borderRadius: '8px 8px 0 0',
                            transition: 'all 0.2s ease',
                            color: 'text.secondary',
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                color: 'primary.main',
                            },
                            '&.Mui-selected': {
                                color: 'primary.main',
                                fontWeight: 600,
                            },
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0',
                        },
                    }}>
                    {visibleTabs.map((item, index) => (
                        <Tab
                            key={item.key}
                            label={
                                screenSizeDownSm ? item.label : customLabel(item.label, item.icon)
                            }
                            id={`simple-tab-${index}`}
                            aria-controls={`simple-tabpanel-${index}`}
                        />
                    ))}
                </Tabs>
            </Box>

            {visibleTabs.map((item, index) => (
                <TabPanel key={item.key} value={value} index={index} disablePadding={item.key === 'log'}>
                    {item.render()}
                </TabPanel>
            ))}
        </Box>
    );
};

export default BookingDetailTabs;
